import { useState } from 'react';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Check, ExternalLink, FileText, ScanLine, X } from 'lucide-react';

import { PageHeader } from '@/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Button } from '@/ui/base/button';
import { Badge } from '@/ui/base/badge';
import { Textarea } from '@/ui/base/textarea';
import { Spinner } from '@/ui/Spinner';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';

import { useExtracoesPendentes, useModerarExtracao, type ExtracaoOcr } from './dados';

/**
 * `/admin/moderacao-ocr` — comparar o documento enviado com o que o OCR leu dele,
 * e aprovar ou reprovar com motivo.
 *
 * Portado do protótipo (`ModeracaoOcrPage.tsx`), com três diferenças que vêm de
 * este sistema não ser aquele:
 *
 * 1. SEM SUPABASE STORAGE. O protótipo buscava a URL pública da imagem no Storage
 *    do Supabase. Aqui não existe (ainda) armazenamento de arquivo nenhum — a
 *    pré-visualização usa `documento.arquivo_url` como a API devolver, pronta
 *    para uso; sem ela, mostra o mesmo aviso de indisponível que o protótipo já
 *    tinha para esse caso.
 * 2. SEM "VER CREDENCIADO". O protótipo linkava para `/credenciados/:slug`, rota
 *    que não existe neste sistema — a tela de detalhe do credenciado é passo
 *    futuro (ver `operacao/credenciados/CredenciadosPage.tsx`). Ligar a um
 *    caminho que não existe é rota órfã, e CLAUDE.md proíbe.
 * 3. "APROVAR"/"REPROVAR" NÃO GRAVAM NADA. `/documentos/ocr/pendentes` e a
 *    moderação em si ainda não existem na API — não há módulo de
 *    documentos/OCR em `apps/api/src/modulos/`. O motivo da reprovação
 *    continua validado de verdade (campo obrigatório); só o passo final mostra
 *    o aviso honesto em vez de fingir que aplicou a decisão (`dados.ts`,
 *    `useModerarExtracao`).
 */
export function ModeracaoOcrPage() {
  const { data: extracoes, isLoading, isError, error, refetch } = useExtracoesPendentes();
  const { moderar } = useModerarExtracao();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');

  const lista = extracoes ?? [];
  const selected: ExtracaoOcr | undefined =
    lista.find((extracao) => extracao.id === selectedId) ?? lista[0];

  const previewUrl = selected?.documento?.arquivo_url ?? null;
  const isPdf = selected?.documento?.arquivo_nome?.toLowerCase().endsWith('.pdf') ?? false;

  const handleAprovar = () => {
    if (!selected) return;
    moderar({ id: selected.id, decisao: 'APROVADO' });
  };

  const handleReprovar = () => {
    if (!selected || !motivo.trim()) return;
    moderar({ id: selected.id, decisao: 'REPROVADO', motivo });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Moderação OCR de Documentos"
        description="Compare a imagem do documento com os dados extraídos automaticamente, aprove ou reprove com motivo."
      />

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível buscar a fila de moderação agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : lista.length === 0 ? (
        <EmptyState
          variant="success"
          icon={ScanLine}
          title="Fila de moderação vazia"
          description="Nenhum documento com OCR aguarda revisão no momento. Atualize a fila para buscar novos envios."
          actionLabel="Atualizar fila"
          onAction={() => void refetch()}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <Card className="lg:max-h-[80vh] lg:overflow-auto">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Fila ({lista.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 p-3">
              {lista.map((extracao) => {
                const active = (selected?.id ?? null) === extracao.id;
                return (
                  <button
                    key={extracao.id}
                    type="button"
                    onClick={() => setSelectedId(extracao.id)}
                    aria-pressed={active}
                    className={`w-full rounded-lg border p-2.5 text-left transition ${
                      active ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <p className="truncate text-sm font-medium">
                      {extracao.documento?.tipo ?? '—'}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {extracao.documento?.credenciado?.nome ?? '—'}
                    </p>
                    <div className="mt-1 flex items-center justify-between">
                      <Badge variant="outline" className="text-[10px]">
                        {Math.round((extracao.confianca ?? 0) * 100)}% conf.
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {format(new Date(extracao.created_at), 'dd/MM HH:mm', { locale: ptBR })}
                      </span>
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {selected && (
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="flex-row items-center justify-between pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    Documento original
                  </CardTitle>
                  {previewUrl && (
                    <Button asChild variant="ghost" size="sm">
                      <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                        Abrir
                      </a>
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {previewUrl ? (
                    isPdf ? (
                      <iframe
                        src={previewUrl}
                        className="h-[60vh] w-full rounded-md border bg-muted"
                        title="Documento"
                      />
                    ) : (
                      <img
                        src={previewUrl}
                        alt="Documento enviado"
                        className="max-h-[60vh] w-full rounded-md border bg-muted object-contain"
                      />
                    )
                  ) : (
                    <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
                      Pré-visualização indisponível
                    </div>
                  )}
                  <div className="mt-3 text-xs text-muted-foreground">
                    Arquivo:{' '}
                    <span className="font-mono">{selected.documento?.arquivo_nome ?? '—'}</span>
                  </div>
                  {selected.documento?.credenciado?.nome && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Credenciado: {selected.documento.credenciado.nome}
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <ScanLine className="h-4 w-4 text-primary" aria-hidden="true" />
                    Dados extraídos por OCR
                    <Badge variant="outline" className="ml-auto text-[10px]">
                      {Math.round((selected.confianca ?? 0) * 100)}% confiança
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5 rounded-md border bg-muted/30 p-3">
                    {Object.entries(selected.campos_extraidos ?? {}).map(([campo, valor]) => (
                      <div key={campo} className="flex items-start justify-between gap-3 text-sm">
                        <span className="capitalize text-muted-foreground">
                          {campo.replace(/_/g, ' ')}
                        </span>
                        <span className="break-all text-right font-mono font-medium">
                          {String(valor)}
                        </span>
                      </div>
                    ))}
                    {Object.keys(selected.campos_extraidos ?? {}).length === 0 && (
                      <p className="text-xs text-muted-foreground">Nenhum campo estruturado.</p>
                    )}
                  </div>

                  {selected.texto_bruto && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground">
                        Ver texto bruto
                      </summary>
                      <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-muted/30 p-3">
                        {selected.texto_bruto}
                      </pre>
                    </details>
                  )}

                  <div className="space-y-2 border-t pt-2">
                    <label htmlFor="motivo-da-reprovacao" className="text-xs font-medium">
                      Motivo (obrigatório para reprovar)
                    </label>
                    <Textarea
                      id="motivo-da-reprovacao"
                      value={motivo}
                      onChange={(evento) => setMotivo(evento.target.value)}
                      placeholder="Ex: documento ilegível; data divergente; assinatura ausente…"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={handleReprovar}
                      disabled={!motivo.trim()}
                    >
                      <X className="mr-1 h-4 w-4" aria-hidden="true" />
                      Reprovar
                    </Button>
                    <Button className="flex-1" onClick={handleAprovar}>
                      <Check className="mr-1 h-4 w-4" aria-hidden="true" />
                      Aprovar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ModeracaoOcrPage;
